import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import CheckinAnalytics from './CheckinAnalytics.jsx';
import { REGION_LABEL } from '../utils/bodyRegions.js';
import './WellnessAnalyticsDashboard.css';

// Charts pull in Observable Plot — lazy so it stays out of the main bundle.
const TeamWellnessCharts = lazy(() => import('./profile/TeamWellnessCharts.jsx'));

// Coach recommendations derived from the UNIFIED, filtered signals (readiness,
// at-risk count, soreness location, check-in rate) — they change with the
// Level/Class/Athlete filter, unlike the old canned RPE cards.
function buildRecs(s = {}) {
  const out = [];
  if (s.avg_readiness != null) {
    out.push({ icon: '🧘', title: 'Readiness', tone: s.avg_readiness >= 70 ? 'good' : s.avg_readiness >= 50 ? 'warn' : 'bad',
      text: s.avg_readiness >= 70 ? `Averaging ${s.avg_readiness}/100 — group is fresh; good day to push.`
        : s.avg_readiness >= 50 ? `Averaging ${s.avg_readiness}/100 — moderate; watch total volume.`
          : `Averaging ${s.avg_readiness}/100 — low; favor technique & recovery over load.` });
  } else {
    out.push({ icon: '🧘', title: 'Readiness', tone: 'muted', text: 'No check-ins in this view yet.' });
  }
  out.push({ icon: s.at_risk_count ? '⚠️' : '✅', title: 'Athletes to watch', tone: s.at_risk_count ? 'warn' : 'good',
    text: s.at_risk_count ? `${s.at_risk_count} flagged — see “Monitor Closely” above.` : 'Nobody flagged in this view.' });
  if (s.injured_count) {
    out.push({ icon: '🏥', title: 'Injuries', tone: 'bad',
      text: `${s.injured_count} athlete${s.injured_count === 1 ? '' : 's'} carrying an injury — check restrictions before loading.` });
  }
  if (s.top_sore_region) {
    out.push({ icon: '🩹', title: 'Soreness', tone: s.top_sore_region.count >= 3 ? 'warn' : 'muted',
      text: `Most-reported: ${REGION_LABEL[s.top_sore_region.key] || s.top_sore_region.key} (${s.top_sore_region.count}×)${s.avg_soreness != null ? ` · avg ${s.avg_soreness}/5` : ''} — add targeted mobility.` });
  } else {
    out.push({ icon: '🩹', title: 'Soreness', tone: (s.avg_soreness || 0) >= 3.5 ? 'warn' : 'good',
      text: s.avg_soreness != null ? `Average soreness ${s.avg_soreness}/5.` : 'No soreness reports in this view.' });
  }
  out.push({ icon: '📋', title: 'Check-in rate', tone: 'muted',
    text: `${s.checked_in || 0} athlete${(s.checked_in || 0) === 1 ? '' : 's'} checked in during this window.` });
  return out;
}

function WellnessAnalyticsDashboard() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30'); // days
  const [athleteFilter, setAthleteFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [athletes, setAthletes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classRoster, setClassRoster] = useState([]); // athletes in the selected class

  // Dashboard data
  const [teamStats, setTeamStats] = useState({
    avgRPE: 0,
    avgFatigue: 0,
    avgSoreness: 0,
    overtrainedAthletes: [],
    recoveryOnTrack: [],
    atRisk: [],
    recentCheckins: []
  });

  useEffect(() => {
    loadDashboardData();
  }, [timeframe, athleteFilter, classFilter, levelFilter]);

  // Filter option lists load once.
  useEffect(() => {
    loadAthletes();
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a class is picked, narrow the Athlete dropdown to that class's roster.
  useEffect(() => {
    if (classFilter === 'all') { setClassRoster([]); return; }
    api.get(`${API_BASE_URL}/api/classes/${classFilter}/roster`)
      .then(r => setClassRoster((Array.isArray(r.data) ? r.data : []).map(x => ({ id: x.athlete_id, name: x.athlete_name }))))
      .catch(() => setClassRoster([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `${API_BASE_URL}/api/v1/wellness/analytics?days=${timeframe}&athlete_id=${athleteFilter}&class_id=${classFilter}&level=${levelFilter}`
      );
      setTeamStats(res.data.summary);
    } catch (err) {
      console.error('Error loading wellness data:', err);
      toast('Error loading wellness data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAthletes = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/athletes`);
      setAthletes(Array.isArray(res.data) ? res.data : (res.data?.athletes || []));
    } catch (err) {
      console.error('Error loading athletes:', err);
    }
  };

  const loadClasses = async () => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/classes`);
      setClasses(Array.isArray(res.data) ? res.data : (res.data?.classes || []));
    } catch (err) {
      console.error('Error loading classes:', err);
    }
  };

  const levels = [...new Set(classes.map(c => c.level).filter(Boolean))].sort();

  if (loading) {
    return <div className="wellness-dashboard loading">Loading wellness data...</div>;
  }

  return (
    <div className="wellness-dashboard">
      <div className="dashboard-header" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1>🧘 Team Wellness Dashboard</h1>
          <p>Monitor athlete health, recovery, and check-ins</p>
        </div>
        <Link to="/wellness/forms/reports" style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '.85rem', color: '#4f46e5', whiteSpace: 'nowrap' }}>📋 Form responses →</Link>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <div className="filter-group">
          <label>Timeframe</label>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Level</label>
          <select value={levelFilter} onChange={(e) => { setLevelFilter(e.target.value); setClassFilter('all'); setAthleteFilter('all'); }}>
            <option value="all">All levels</option>
            {levels.map(lv => <option key={lv} value={lv}>{lv}</option>)}
          </select>
        </div>

        <div className="filter-group">
          <label>Class</label>
          <select value={classFilter} onChange={(e) => { setClassFilter(e.target.value); setAthleteFilter('all'); }}>
            <option value="all">{levelFilter === 'all' ? 'Whole gym' : 'All in level'}</option>
            {classes
              .filter(c => levelFilter === 'all' || String(c.level) === String(levelFilter))
              .map(c => (
                <option key={c.id} value={c.id}>{c.class_name || c.name || `Class ${c.id}`}</option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Athlete</label>
          <select value={athleteFilter} onChange={(e) => setAthleteFilter(e.target.value)}>
            <option value="all">{classFilter !== 'all' ? 'All in class' : levelFilter !== 'all' ? 'All in level' : 'All athletes'}</option>
            {(classFilter !== 'all' ? classRoster : athletes).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Result count — so the filter is visibly doing something */}
      <div style={{ fontSize: '.82rem', color: '#64748b', margin: '2px 2px 12px' }}>
        Showing <strong>{(teamStats.recentCheckins || []).length}</strong> recent check-in{(teamStats.recentCheckins || []).length === 1 ? '' : 's'}
        {' · '}<strong>{(teamStats.atRisk || []).length}</strong> to watch
        {' · '}<strong>{(teamStats.recoveryOnTrack || []).length}</strong> on track
        {(teamStats.injured_count || 0) > 0 && <>{' · '}<strong style={{ color: '#b91c1c' }}>{teamStats.injured_count}</strong> injured</>}
        {(levelFilter !== 'all' || classFilter !== 'all' || athleteFilter !== 'all') && <span style={{ color: '#4f46e5' }}> — filtered</span>}
      </div>

      {/* Real, data-backed team wellness: readiness trend + soreness heat map */}
      <Suspense fallback={<div style={{ padding: '1rem', color: '#9ca3af' }}>Loading charts…</div>}>
        <TeamWellnessCharts timeframe={timeframe} athleteId={athleteFilter} classId={classFilter} level={levelFilter} />
      </Suspense>

      {/* Attendance check-in question trends — honors the dashboard filters */}
      <CheckinAnalytics days={Number(timeframe)} athleteId={athleteFilter} classId={classFilter} level={levelFilter} />

      {/* Athlete Status Sections */}
      <div className="status-sections">
        {/* Recent Check-Ins — who submitted a daily readiness check-in */}
        <div className="status-card success">
          <h3>✅ Recent Check-Ins</h3>
          <p className="card-count">{(teamStats.recentCheckins || []).length} check-in(s)</p>
          <div className="athlete-list">
            {(teamStats.recentCheckins || []).length === 0 ? (
              <p style={{ color: '#666' }}>No check-ins in this window yet. Athletes submit these from their Daily Check-In.</p>
            ) : (
              teamStats.recentCheckins.map((c, i) => (
                <div key={i} className="athlete-item">
                  {c.athlete_id
                    ? <Link className="athlete-name" to={`/athlete-card/${c.athlete_id}?tab=wellness`}>{c.athlete_name}</Link>
                    : <span className="athlete-name">{c.athlete_name}</span>}
                  <div className="athlete-metrics">
                    <span className="metric">Readiness: {c.readiness_score != null ? `${c.readiness_score}/100` : '—'}</span>
                    <span className="metric">{c.checkin_date}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* On Track */}
        {teamStats.recoveryOnTrack && teamStats.recoveryOnTrack.length > 0 && (
          <div className="status-card success">
            <h3>✅ On Track</h3>
            <p className="card-count">{teamStats.recoveryOnTrack.length} athletes</p>
            <div className="athlete-list">
              {teamStats.recoveryOnTrack.map(a => (
                <div key={a.id} className="athlete-item">
                  <Link className="athlete-name" to={`/athlete-card/${a.id}?tab=wellness`}>{a.name}</Link>
                  <div className="athlete-metrics">
                    <span className="metric">Readiness: {a.readiness}/100</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* At Risk */}
        {teamStats.atRisk && teamStats.atRisk.length > 0 && (
          <div className="status-card caution">
            <h3>⚠️ Monitor Closely</h3>
            <p className="card-count">{teamStats.atRisk.length} athletes</p>
            <div className="athlete-list">
              {teamStats.atRisk.map(a => (
                <div key={a.id} className="athlete-item">
                  <Link className="athlete-name" to={`/athlete-card/${a.id}?tab=wellness`}>{a.name}</Link>
                  <div className="athlete-metrics">
                    <span className="metric">{a.reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Injuries & Clearances — the medical tie-in, same cohort filter */}
        {teamStats.injuries && teamStats.injuries.length > 0 && (
          <div className="status-card medical">
            <h3>🏥 Injuries &amp; Clearances</h3>
            <p className="card-count">{teamStats.injuries.length} carrying an injury</p>
            <div className="athlete-list">
              {teamStats.injuries.map((inj, i) => (
                <div key={i} className="athlete-item">
                  <Link className="athlete-name" to={`/athlete-card/${inj.athlete_id}?tab=overview`}>{inj.name}</Link>
                  <div className="athlete-metrics">
                    <span className="metric">{[inj.injury_type, inj.body_part].filter(Boolean).join(' · ') || 'injury'}{inj.severity ? ` (${inj.severity})` : ''}</span>
                    {inj.restricted && <span className="metric" style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }}>⛔ restricted</span>}
                    {inj.return_date && <span className="metric">back ~{new Date(inj.return_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recommendations — derived from the filtered, unified check-in signals */}
      <div className="recommendations-section">
        <h2>💡 Coach Recommendations</h2>
        <div className="recommendation-grid">
          {buildRecs(teamStats).map((r, i) => (
            <div key={i} className={`recommendation-card rec-${r.tone}`}>
              <span className="rec-icon">{r.icon}</span>
              <h4>{r.title}</h4>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WellnessAnalyticsDashboard;
